import { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from 'react-router-dom'
import { useQuery } from "@tanstack/react-query"
import { videoService } from "../services/video"
import { Loader } from "../cmps/loader"
import { ErrorPage } from "./error"
import { VideoCard } from "../cmps/video-card"
import { Login } from "./login"
import { Filter } from '../cmps/filter'
import { rolesRank } from '../services/data'
import { setPreview, setPopup, setSearch } from '../store/reducers/general'
import { rolesColors } from '../services/data'
import { calculateDaysDifference } from '../services/util'

export function Home() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const user = useSelector(state => state.user)
    const { currentTeam, teams } = user
    const { search } = useSelector(state => state.general)
    const roles = currentTeam?.members?.find(member => member._id === user._id)?.roles || []
    const isAdmin = roles.includes('admin')

    const [filter, setFilter] = useState(isAdmin ? 'awaiting-feedback' : '')
    const [archivedVideos, setArchivedVideos] = useState({})
    const [isArchive, setIsArchive] = useState(false)

    let { data: videos, isLoading, isError, refetch } = useQuery({
        queryKey: [currentTeam?._id],
        queryFn: async () => {
            if (!currentTeam?._id) return []
            const vids = await videoService.getVideos(currentTeam?._id)
            return vids
        },
        staleTime: 60 * 60 * 1000,
        refetchInterval: 60 * 60 * 1000,
        cacheTime: 120 * 60 * 1000,
        refetchOnMount: 'always',
        retry: 3,
        refetchOnWindowFocus: true,
        enabled: !!currentTeam?._id
    })

    useEffect(() => {
        refetch()
    }, [user._id])

    const handleArchive = async () => {
        try {
            if (!isArchive && !archivedVideos[currentTeam?._id]) {
                const archived = await videoService.getArchivedVideos(currentTeam?._id)
                setArchivedVideos({ ...archivedVideos, [currentTeam?._id]: archived })
            }
            if (!isArchive) setFilter('')
            setIsArchive(!isArchive)
        }
        catch (err) {
            console.log(err)
        }
    }

    const getColorEditor = (level) => {
        if (!roles.includes('thumbnailEditor')) return ''
        if (level === 'researcher') return 'green-border'
        if (level === 'scripter') return 'yellow-border'
        if (level === 'producer') return 'red-border'
        return ''
    }

    const getColorAdmin = (level) => {
        return rolesColors[level] ? rolesColors[level] + '-border' : ''
    }

    const videosToRender = useMemo(() => {
        if (!videos) return []
        if (roles.includes('thumbnailEditor')) {
            let vd = videos.map(video => ({ ...video, className: getColorEditor(video.status.level) })).sort((a, b) => rolesRank[a.status.level] - rolesRank[b.status.level])
            if (search) return vd.filter(video => video.ideaMaker.titles.some(title => title.includes(search)))
            return vd
        }

        let v = isArchive ? (archivedVideos[currentTeam?._id] || []) : videos
        if (filter) {
            if (filter === 'awaiting-feedback') v = videos.filter(video => video.status.status === 'forward-as-success' || video.status.status === 'forward-as-failure')
            else if (filter === 'admin-jobs') v = videos.filter(video => video.status.workerId === user._id)
            else v = videos.filter(video => video.status.level === filter)
        }
        if (search) v = v.filter(video => video.ideaMaker.titles.some(title => title.includes(search) || video.ideaMaker.location.includes(search)))
            
        return v.map(video => ({ ...video, className: getColorAdmin(video.status.level), workDays: calculateDaysDifference(video.status.recievedAt) }))
    }, [videos, roles, filter, search, isArchive, archivedVideos])


    if (!user.name) return <Login />

    if (!teams.length) return <div className='home'>
        <div className='create-buttton-wrapper'>
            <h3>אינך חבר באף צוות עדיין.</h3>
            <button style={{ marginTop: '20px' }} onClick={() => navigate('/new-team')}>יצירת צוות חדש</button>
        </div>
    </div>

    if (isLoading) return <Loader />

    if (isError) return <ErrorPage />

    return <div className='home'>
        <div className='admin-line'>
            <div className='channel-info'>
                <img src={currentTeam.channelImage} alt={currentTeam.channelName} />
                <h1>{currentTeam.channelName}</h1>
            </div>

            <div className='admin-buttons'>
                {videosToRender.length > 2 && <button className='btn' onClick={() => dispatch(setPreview(videosToRender))}>תצוגת יוטיוב</button>}
                {isAdmin && <button className={`btn ${isArchive ? 'back-main7' : ''}`} onClick={handleArchive}>ארכיון</button>}
                <button className='btn' onClick={() => dispatch(setPopup(roles.includes('thumbnailEditor') ? 'urgency' : 'roles'))}>מקרא צבעים</button>
            </div>

        </div>

        {(roles.length >= 2 || isAdmin) && <Filter filter={filter} setFilter={setFilter} isAdmin={isAdmin} roles={roles} />}

        <div className='videos-wrapper'>
            {videosToRender.map((video) => <VideoCard key={video._id} video={video} className={video.className || ''} isAdmin={isAdmin} displayRole={isAdmin && (filter === 'awaiting-feedback' || filter === 'archived' || filter === '')} />)}
        </div>

        {(search && !videosToRender.length) && <p style={{ fontSize: '20px' }}>אין סרטונים המתאימים לחיפוש שבחרת. <span className='clickable underline main7' style={{ display: 'block' }} onClick={() => { dispatch(setSearch('')) }}>נקה חיפוש</span></p>}

        {(videos?.length === 0 || (filter === 'awaiting-feedback' && videosToRender.length === 0)) &&
            <div className='create-buttton-wrapper'>
                <img src={require('../style/imgs/idea.webp')} alt='no-videos' />
                <h3>כאן יופיעו הרעיונות בתחום האחריות שלך</h3>
                <p>אין לך שום משימות כרגע - בוא תיצור רעיון חדש</p>
                <button onClick={() => navigate('/create')}>יצירת רעיון חדש</button>
            </div>}
    </div>
}